import { Component, OnInit, Output, EventEmitter, Input, ChangeDetectorRef, Injector } from '@angular/core';
import { BaseControlComponent } from '../base-control/base-control.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { LanguageOption } from '../../models/language.model';

/**
 * LanguageSelectComponent provides a dropdown for selecting a document language code.
 * It integrates with Angular forms, supports validation, and emits value changes.
 * This component does not change the application's language.
 */
@Component({
	selector: 'app-language-select',
	templateUrl: './language-select.component.html',
	styleUrls: ['./language-select.component.scss'],
	providers: [
		MessageService,
	]
})
export class LanguageSelectComponent extends BaseControlComponent implements OnInit {

	/**
	 * The list of available language options to display in the dropdown.
	 * Expected format: [{ code: 'en', name: 'English' }, { code: 'he', name: 'Hebrew' }, ...]
	 * @type {LanguageOption[]}
	 */
	@Input() availableLanguages: LanguageOption[] = [];

	/**
	 * Emits the selected language code (e.g., 'en', 'he') when the selection changes.
	 * @type {EventEmitter<string | null>}
	 */
	@Output() languageChange = new EventEmitter<string | null>();

	/**
	 * Creates an instance of LanguageSelectComponent.
	 * @param {Injector} injector - Injector for dependency injection.
	 * @param {TranslateService} translate - Service for handling translations.
	 * @param {ChangeDetectorRef} cdr - Service for triggering change detection.
	 * @param {MessageService} messageService - Service for displaying messages (toasts).
	 */
	constructor(
		protected override injector: Injector,
		protected override translate: TranslateService,
		protected override cdr: ChangeDetectorRef,
		protected override messageService: MessageService
	) {
		super(injector, translate, cdr, messageService);
	}

	/**
	 * Angular lifecycle hook that is called after data-bound properties are initialized.
	 * Calls the base class ngOnInit and subscribes to parent control changes if needed.
	 * @override
	 */
	override ngOnInit(): void {
		super.ngOnInit();
		if (this.control) {
			this.parentValueSub = this.control.valueChanges.subscribe(value => {
});
		} else if (this.debug) {
			console.warn(`[DEBUG/LanguageSelect] Control not found for ${this.controlName}`);
		}

		let valueToSet = this.availableLanguages[1].code;
		this.control?.setValue(valueToSet, { emitEvent: false });
		this.languageChange.emit(valueToSet);
	}

	/**
	 * Handles changes from the internal dropdown component.
	 * Propagates the change to the parent form via ControlValueAccessor
	 * and also emits the separate languageChange event.
	 * @param {string | null} selectedCode - The code of the selected language (e.g., 'en'), or null if cleared.
	 */
	onInternalChange(selectedCode: string | null): void {
this.propagateChange(selectedCode);
		this.languageChange.emit(selectedCode);
		this.markAsInteracted();
	}
}
